import { authFetch, fetchWithAuth, BASE_BACKEND_URL } from '../../auth'
import React, { useState, useEffect, useCallback, useRef } from 'react'

import Pagination from '../../layout/Pagination'
import usePagination from '../../layout/usePagination'
import LoadingScreen from '../../components/LoadingScreen'
import dayjs from '../../utils/dayjsConfig'

import { STATUS_OPTIONS, STATUS_UI_MAP } from './constants/ocConstants'

import { DEFAULT_PER_PAGE } from '../../constants/constant'

import IndicatorSummary from '../../components/indicators/IndicatorSummary'
import { OC_INDICATORS_CONFIG } from './constants/ocConstants'

import { Box, Typography } from '@mui/material'
import OcDateRangeCard from './components/DateHeader/OcDateRangeCard'
import OcFiltersBar from './components/filters/OcFiltersBar'
import OcOrdersTable from './components/table/OcOrdersTable'
import ExportSingleButton from '../PocReports/components/ExportSingleButton'
import { downloadOverviewCsv } from './helpers/downloads/downloadOverviewCsv'

const OrderOcIndex = () => {
  const abortControllerRef = useRef(new AbortController())
  const dataAbortRef = useRef(null)
  const filtersAbortRef = useRef(null)
  const indicatorsAbortRef = useRef(null)
  const title = 'Órdenes de compra'
  const [data, setData] = useState([])
  const [filters, setFilters] = useState([])
  const [filterData, setFilterData] = useState([])
  const [indicatorData, setIndicatorData] = useState([])
  const [labelToIdMapping, setLabelToIdMapping] = useState({})
  const [allSelectedOptions, setAllSelectedOptions] = useState(new Map())
  const [filterQuery, setFilterQuery] = useState('')
  const [processedFilter, setProcessedFilter] = useState(null)
  const [filterVersion, setFilterVersion] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [triggerSearch, setTriggerSearch] = useState(false)
  const today = dayjs().startOf('day')
  const DEFAULT_DATES = [today, today]
  const [dateQuery, setDateQuery] = useState(null)
  const [selectedDates, setSelectedDates] = useState(null)
  const [alertMessage, setAlertMessage] = useState(null)
  const [alertType, setAlertType] = useState('success')
  const [sendingIds, setSendingIds] = useState(new Set())
  const [loadingDownload, setLoadingDownload] = useState(false)
  const sendingIdsRef = useRef(new Set())

  const { pagination, setPagination, handlePaginationClick } = usePagination({
    per_page: DEFAULT_PER_PAGE
  })

  const updateAllSelectedOptions = (identifier, selectedLabels) => {
    setAllSelectedOptions(prev => {
      const updated = new Map(prev)
      const labels = Array.isArray(selectedLabels)
        ? selectedLabels
        : Array.from(selectedLabels || [])

      updated.set(
        identifier,
        labels.map(label => labelToIdMapping[label])
      )

      return updated
    })
  }

  const buildFiltersQuery = () => {
    let query = ''

    allSelectedOptions.forEach((value, key) => {
      if (!value.length) return

      if (key === 'CADENA') query += `&buyer_id=${value.join(',')}`
      if (key === 'SUCURSAL') query += `&client_uid=${value.join(',')}`
      if (key === 'USUARIO') query += `&user_uid=${value.join(',')}`
      if (key === 'ESTADO') query += `&status=${value.join(',')}`
    })

    pagination.current_page = 1
    setFilterQuery(query)
  }

  const prepareFilterData = () => {
    if (!filterData) return

    const mapping = {}

    const chainOptions = (filterData.buyer || []).map(b => {
      const label = `${b.buyer_id} - ${b.buyer_name}`
      mapping[label] = b.buyer_id
      return label
    })

    const clientOptions = (filterData.client || []).map(c => {
      const label = `${c.client_uid} - ${c.client_name}`
      mapping[label] = c.client_uid
      return label
    })

    const userOptions = (filterData.user || []).map(u => {
      const label = `${u.user_uid} - ${u.user_name}`
      mapping[label] = u.user_uid
      return label
    })

      const statusOptions = (filterData.status || []).map(statusKey => {
        const option = STATUS_OPTIONS.find(o => o.key === statusKey)

        if (!option) return null

        mapping[option.label] = option.key
        return option.label
      }).filter(Boolean)

    setFilters([
      { key: 'CADENA', value: chainOptions },
      { key: 'SUCURSAL', value: clientOptions },
      { key: 'USUARIO', value: userOptions },
      { key: 'ESTADO', value: statusOptions }
    ])

    setLabelToIdMapping(mapping)
  }

  const handleProcessedApply = ({ operator, value }) => {
    let query = ''

    if (operator === 'gt') query = `&process_gt=${value}`
    if (operator === 'lt') query = `&process_lt=${value}`

    setProcessedFilter({ operator, value })
    setFilterQuery(prev =>
      prev.replace(/&process_(gt|lt)=\d+/g, '') + query
    )

    pagination.current_page = 1
  }

  const handleDateFilter = (dates) => {
    if (!dates?.length) return

    const start = dayjs.min(dates).startOf('day')
    const end = dayjs.max(dates).endOf('day')

    setSelectedDates(dates)
    setDateQuery(
      `&start_date=${start.format('YYYY-MM-DD HH:mm:ss')}` +
      `&end_date=${end.format('YYYY-MM-DD HH:mm:ss')}`
    )

    pagination.current_page = 1
  }

  const resetReactFilters = () => {
    setAllSelectedOptions(new Map())
    setFilterQuery('')
    setSearchInput('')
    setProcessedFilter(null)
    setTriggerSearch(v => !v)
    setFilterVersion(v => v + 1)
    pagination.current_page = 1
  }

  const resetAll = () => {
    resetReactFilters()
    setSelectedDates(DEFAULT_DATES)
    handleDateFilter(DEFAULT_DATES)
  }

  const fetchFilters = async () => {
    if (filtersAbortRef.current) {
      filtersAbortRef.current.abort()
    }

    const controller = new AbortController()
    filtersAbortRef.current = controller

    try {
      const response = await authFetch(
        `${BASE_BACKEND_URL}/poc/overview/filters?${dateQuery || ''}`,
        {
          headers: {
          },
          signal: controller.signal
        }
      )

      setFilterData(await response.json())
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('fetchFilters error', err)
      }
    }
  }

  const fetchIndicators = async () => {
    if (indicatorsAbortRef.current) {
      indicatorsAbortRef.current.abort()
    }

    const controller = new AbortController()
    indicatorsAbortRef.current = controller

    try {
      const response = await authFetch(
        `${BASE_BACKEND_URL}/poc/overview/indicators?${dateQuery || ''}`,
        {
          headers: {
          },
          signal: controller.signal
        }
      )

      setIndicatorData(await response.json())
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('fetchIndicators error', err)
      }
    }
  }

  const fetchData = useCallback(async () => {
    if (dataAbortRef.current) {
      dataAbortRef.current.abort()
    }

    const controller = new AbortController()
    dataAbortRef.current = controller

    try {
      const { current_page, per_page } = pagination

      let processedQuery = ''
      if (processedFilter) {
        processedQuery =
          processedFilter.operator === 'gt'
            ? `&process_gt=${processedFilter.value}`
            : `&process_lt=${processedFilter.value}`
      }

      const rawResponse = await fetchWithAuth(
        `${BASE_BACKEND_URL}/poc/overview?page=${current_page}&per_page=${per_page}` +
        `${filterQuery}${processedQuery}${dateQuery || ''}` +
        `${searchInput ? `&q=${encodeURIComponent(searchInput)}` : ''}`,
        {
          signal: controller.signal
        }
      )

      const response = rawResponse ?? []

      setPagination(prev => ({
        ...prev,
        total_pages: Math.ceil(response.total_records / prev.per_page)
      }))

      setData(response.results)
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('fetchData error', err)
      }
    }
  }, [
    pagination.current_page,
    filterQuery,
    processedFilter,
    dateQuery,
    searchInput
  ])

  const handleSend = async (model) => {
    if (sendingIdsRef.current.has(model.id)) return

    sendingIdsRef.current.add(model.id)
    setSendingIds(prev => new Set(prev).add(model.id))

    try {
      const response = await authFetch(
        `${BASE_BACKEND_URL}/poc/orders/${model.order_id}/send_to_erp`,
        {
          method: 'GET',
          headers: {
          }
        }
      )

      const result = await response.json()

      if (!result?.success) {
        throw new Error('send failed')
      }

      setAlertType('success')
      setAlertMessage(
        result?.already_processing
          ? 'La orden ya tiene un envío en proceso. Espera la respuesta antes de intentar nuevamente.'
          : 'La orden se está enviando al ERP. Este proceso puede tardar unos minutos.'
      )
      setTimeout(async () => {
        await fetchData()
        sendingIdsRef.current.delete(model.id)

        setSendingIds(prev => {
          const next = new Set(prev)
          next.delete(model.id)
          return next
        })
      }, 5000)

    } catch {
      setAlertType('danger')
      setAlertMessage('No fue posible enviar la orden al ERP. Intente nuevamente en unos minutos.')
      sendingIdsRef.current.delete(model.id)

      setSendingIds(prev => {
        const next = new Set(prev)
        next.delete(model.id)
        return next
      })
    }
  }

  const handleIgnore = async (model) => {
    try {
      const response = await authFetch(
        `${BASE_BACKEND_URL}/poc/purchase_orders/ignore?po_db_ref=${encodeURIComponent(model.db_ref)}`,
        {
          method: 'POST',
          headers: {
          }
        }
      )

      const result = await response.json()

      if (response.ok) {
        setAlertType('success')
        setAlertMessage('La orden fue rechazada correctamente.')
        fetchData()
      } else {
        setAlertType('danger')
        setAlertMessage(result?.error || 'No fue posible rechazar la orden.')
      }
    } catch {
      setAlertType('danger')
      setAlertMessage('Ocurrió un error inesperado al rechazar la orden.')
    }
  }

  const handleReprocess = async (model) => {
    try {
      const response = await fetchWithAuth(
        `${BASE_BACKEND_URL}/poc/orders/${model.order_id}/reprocess`
      )

      if (response?.success) {
        setAlertType('success')
        setAlertMessage('La orden fue reprocesada correctamente.')
        fetchData()
      } else {
        setAlertType('danger')
        setAlertMessage('No fue posible reprocesar la orden. Intente nuevamente.')
      }
    } catch {
      setAlertType('danger')
      setAlertMessage('Ocurrió un error inesperado al reprocesar la orden.')
    }
  }

  const handleSearch = e => {
    setSearchInput(e.target.value)
    pagination.current_page = 1
  }

  const handleEnterSearch = e => {
    if (e.key === 'Enter') {
      pagination.current_page = 1
      fetchData()
    }
  }

  const clearSearchOnly = () => {
    setSearchInput('')
    pagination.current_page = 1
    setTriggerSearch(v => !v)
  }

  const handleDownload = async () => {
    let processedQuery = ''
    if (processedFilter) {
      processedQuery =
        processedFilter.operator === 'gt'
          ? `&process_gt=${processedFilter.value}`
          : `&process_lt=${processedFilter.value}`
    }

    const searchQuery = searchInput
      ? `&q=${encodeURIComponent(searchInput)}`
      : ''

    setLoadingDownload(true)
    await downloadOverviewCsv(`${filterQuery}${processedQuery}${dateQuery || ''}${searchQuery}`)
    setLoadingDownload(false)
  }

  const filtersArray = Array.isArray(filters) ? filters : Array.from(filters || [])
  const areFiltersAvailable = filtersArray.some(filter => Array.isArray(filter.value) && filter.value.length > 0)

  useEffect(() => {
    fetchFilters()
    fetchIndicators()
  }, [dateQuery])

  useEffect(() => {
    prepareFilterData()
  }, [filterData])

  useEffect(() => {
    if (allSelectedOptions.size) buildFiltersQuery()
  }, [allSelectedOptions])

  useEffect(() => {
    fetchData()
  }, [pagination.current_page, filterQuery, dateQuery, triggerSearch])

  useEffect(() => {
    if (selectedDates) return

    setSelectedDates(DEFAULT_DATES)
    handleDateFilter(DEFAULT_DATES)
  }, [])

  useEffect(() => {
    return () => {
      dataAbortRef.current?.abort()
      filtersAbortRef.current?.abort()
      indicatorsAbortRef.current?.abort()
    }
  }, [])

  if (!data) return <LoadingScreen text="Cargando..." />

  return (
    <div>
      <div>
        <div>
          <>
            {alertMessage && (
              <div
                className={`alert alert-${alertType} d-flex justify-content-between align-items-center`}
              >
                <strong className="ml-20">{alertMessage}</strong>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setAlertMessage(null)}
                />
              </div>
            )}
            <Box
              sx={{
                px: '75px',
                pr: '95px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                mt: 3
              }}
            >
              <Typography variant="h5" sx={{ fontSize: 28 }}>
                {title}
              </Typography>
              <ExportSingleButton
                label={loadingDownload ? 'Descargando...' : 'Descargar'}
                disabled={loadingDownload || !dateQuery || !data.length}
                onClick={handleDownload}
              />
            </Box>
            <div className="index-table">
              <div className="show-area">
                <div className="mb-20">
                  <OcDateRangeCard
                    value={selectedDates}
                    defaultDates={DEFAULT_DATES}
                    onChange={handleDateFilter}
                    onResetAll={resetAll}
                  />
                </div>
                <IndicatorSummary
                  config={OC_INDICATORS_CONFIG}
                  values={{
                    received: indicatorData?.received,
                    processed: indicatorData?.processed,
                    error: indicatorData?.error,
                    reprocessed: indicatorData?.reprocessed,
                    avgProcessed: indicatorData?.avg_processed
                  }}
                />
                <OcFiltersBar
                  filters={filters}
                  filterVersion={filterVersion}
                  onFilterChange={updateAllSelectedOptions}
                  onProcessedApply={handleProcessedApply}
                  searchInput={searchInput}
                  onSearchChange={handleSearch}
                  onSearchEnter={handleEnterSearch}
                  onReset={resetReactFilters}
                  onClearSearch={clearSearchOnly}
                  areFiltersAvailable={areFiltersAvailable}
                  allSelectedOptions={allSelectedOptions}
                  processedFilter={processedFilter}
                />
                <OcOrdersTable
                  data={data}
                  statusUiMap={STATUS_UI_MAP}
                  sendingIds={sendingIds}
                  onSend={handleSend}
                  onIgnore={handleIgnore}
                  onReprocess={handleReprocess}
                />

              </div>
            </div>
            <nav className="index-table">
              {pagination.total_pages > 1 && (
                <div onClick={handlePaginationClick}>
                  <Pagination
                    current_page={pagination.current_page}
                    total_pages={pagination.total_pages}
                  />
                </div>
              )}
            </nav>
          </>
        </div>
      </div>
    </div>
  )
}
export default OrderOcIndex
